/**
 * Top toolbar for the bookings calendar — page heading, prev/today/next, range
 * pills (14/30/60 days), and a refresh button. Pure presentational; all gesture
 * + data state lives in the parent.
 */
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, RefreshCw, Loader2 } from "lucide-react";

export default function CalendarToolbar({
  carsCount,
  bookingsCount,
  windowDays,
  setWindowDays,
  onPrev,
  onToday,
  onNext,
  onRefresh,
  refreshing,
  gearboxFilter,
  setGearboxFilter,
}) {
  // Iter 74j — quick gearbox filter buttons. Toggle behaviour: clicking the
  // active pill clears the filter. All three colours match the calendar-row
  // chip palette (neutral/blue/amber) for visual consistency.
  const gearboxOptions = [
    { key: "", label: "All", active: "bg-neutral-900 text-white border-neutral-900", idle: "border-border text-neutral-600 hover:border-neutral-400" },
    { key: "A", label: "A", active: "bg-blue-600 text-white border-blue-600", idle: "border-blue-200 text-blue-700 hover:border-blue-400" },
    { key: "M", label: "M", active: "bg-amber-500 text-white border-amber-500", idle: "border-amber-200 text-amber-700 hover:border-amber-400" },
  ];
  return (
    <div className="flex items-center justify-between flex-wrap gap-3 px-5 py-3 border-b border-border bg-neutral-50/40">
      <div className="flex items-center gap-2">
        <CalendarIcon className="h-4 w-4 text-neutral-500" />
        <h3 className="font-display text-lg font-medium">Bookings calendar</h3>
        <span className="text-xs text-neutral-400 ml-1">
          {carsCount} car{carsCount === 1 ? "" : "s"} · {bookingsCount} active booking{bookingsCount === 1 ? "" : "s"}
        </span>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        <Button variant="ghost" size="sm" onClick={onPrev} className="rounded-full h-8" data-testid="cal-prev">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onToday} className="rounded-full h-8 text-xs" data-testid="cal-today">
          Today
        </Button>
        <Button variant="ghost" size="sm" onClick={onNext} className="rounded-full h-8" data-testid="cal-next">
          <ChevronRight className="h-4 w-4" />
        </Button>
        <div className="ml-2 inline-flex rounded-full border border-border p-0.5 bg-white">
          {/* Iter 98 — view presets: Today / 3 Days / Week / 2 Weeks / Month. */}
          {[[1, "Today"], [3, "3d"], [7, "Week"], [14, "14d"], [30, "Month"]].map(([n, label]) => (
            <button
              key={n}
              onClick={() => setWindowDays(n)}
              className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition ${windowDays === n ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-700"}`}
              data-testid={`cal-range-${n}`}
            >
              {label}
            </button>
          ))}
        </div>
        {/* Iter 74j — Gearbox quick-filter (A / M / All). Clicking the same
            pill again clears the filter. Only rendered when the parent
            actually wires up the state (backwards-compatible). */}
        {setGearboxFilter && (
          <div
            className="ml-2 inline-flex rounded-full border border-border p-0.5 bg-white"
            title="Filter by gearbox"
            data-testid="cal-gearbox-filter"
          >
            {gearboxOptions.map((opt) => {
              const isActive = (gearboxFilter || "") === opt.key;
              return (
                <button
                  key={opt.key || "all"}
                  type="button"
                  onClick={() => setGearboxFilter(isActive ? "" : opt.key)}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full border transition min-w-[28px] ${isActive ? opt.active : opt.idle}`}
                  data-testid={`cal-gearbox-filter-${opt.key || "all"}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
        <Button variant="ghost" size="sm" onClick={onRefresh} className="rounded-full h-8 ml-1" data-testid="cal-refresh" aria-label="Refresh">
          {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </div>
    </div>
  );
}
